/** Fields that drive the collab room sidebar — hashed for cheap change detection. */
export type CollabRoomSyncSnapshot = {
  status: string;
  pendingParty: string;
  currentOfferId: string | null;
  offerVersion: number | null;
  offerAmount: number | null;
  offerStatus: string | null;
  offerExpiresAt: string | null;
  termsId: string | null;
  milestones: { id: string; status: string }[];
  proofs: { id: string; status: string; milestoneId: string }[];
  latestDraftId: string | null;
  latestDraftApprovedAt: string | null;
  latestDraftAutoApproveAt: string | null;
  latestPublishedId: string | null;
  latestPublishedUrl: string | null;
  latestPublishedAutoConfirmAt: string | null;
  paymentDeadline: string | null;
  liveUntil: string | null;
  lastMessageId: string | null;
  lastMessageAt: string | null;
};

export function collabRoomRevision(snapshot: CollabRoomSyncSnapshot): string {
  return JSON.stringify(snapshot);
}

/**
 * The same row reaches this function as a Date straight from the database and
 * as an ISO string once it has crossed an HTTP boundary. Both spellings have to
 * hash identically: the initial revision is built server-side from JSON while
 * the poll compares against one built from live rows, so a mismatch would read
 * as a change on every tick and refresh the page forever.
 */
function isoOrNull(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  return typeof value === "string" ? value : value.toISOString();
}

/** Inbox rail / work history — anything that would change a row preview or filter. */
export function collabInboxRevision(
  rows: {
    id: string;
    status: string;
    displayStatus: string;
    pendingParty: string;
    paymentDue: boolean;
    lastMessageAt: Date | string | null;
    timerDeadline: Date | string | null;
    timerLabel: string | null;
  }[],
): string {
  return JSON.stringify(
    rows.map((row) => ({
      id: row.id,
      status: row.status,
      displayStatus: row.displayStatus,
      pendingParty: row.pendingParty,
      paymentDue: row.paymentDue,
      lastMessageAt: isoOrNull(row.lastMessageAt),
      timerDeadline: isoOrNull(row.timerDeadline),
      timerLabel: row.timerLabel,
    })),
  );
}

export function snapshotFromCollabRoom(room: {
  collab: {
    status: string;
    pendingParty: string;
    currentOfferId: string | null;
    termsId: string | null;
    paymentDeadline: Date | null;
    liveUntil: Date | null;
  };
  currentOffer: {
    id: string;
    version: number;
    amountTotal: number;
    status: string;
    expiresAt: Date | null;
  } | null;
  terms: { id: string } | null;
  milestones: { id: string; status: string }[];
  proofs: { id: string; status: string; milestoneId: string }[];
  latestDraft: { id: string; approvedAt: Date | null; autoApproveAt: Date | null } | null;
  latestPublished: {
    id: string;
    publishedUrl: string | null;
    autoApproveAt: Date | null;
  } | null;
  lastMessage: { id: string; createdAt: Date } | null;
}): CollabRoomSyncSnapshot {
  return {
    status: room.collab.status,
    pendingParty: room.collab.pendingParty,
    currentOfferId: room.collab.currentOfferId,
    offerVersion: room.currentOffer?.version ?? null,
    offerAmount: room.currentOffer?.amountTotal ?? null,
    offerStatus: room.currentOffer?.status ?? null,
    offerExpiresAt: isoOrNull(room.currentOffer?.expiresAt),
    termsId: room.terms?.id ?? room.collab.termsId,
    milestones: room.milestones
      .map((m) => ({ id: m.id, status: m.status }))
      .sort((a, b) => a.id.localeCompare(b.id)),
    proofs: room.proofs
      .map((p) => ({
        id: p.id,
        status: p.status,
        milestoneId: p.milestoneId,
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
    latestDraftId: room.latestDraft?.id ?? null,
    latestDraftApprovedAt: isoOrNull(room.latestDraft?.approvedAt),
    latestDraftAutoApproveAt: isoOrNull(room.latestDraft?.autoApproveAt),
    latestPublishedId: room.latestPublished?.id ?? null,
    latestPublishedUrl: room.latestPublished?.publishedUrl ?? null,
    latestPublishedAutoConfirmAt: isoOrNull(room.latestPublished?.autoApproveAt),
    paymentDeadline: isoOrNull(room.collab.paymentDeadline),
    liveUntil: isoOrNull(room.collab.liveUntil),
    lastMessageId: room.lastMessage?.id ?? null,
    lastMessageAt: isoOrNull(room.lastMessage?.createdAt),
  };
}
